/**
 * Track builder.
 *
 * Converts a `TrackDef` (data) into everything the simulation and renderer
 * need:
 *   - a rasterised surface grid used for physics (grip, drag, particles),
 *   - a solid-wall mask used for collisions,
 *   - a resampled centreline with curvature,
 *   - a relaxed racing line with corner radii for the AI,
 *   - checkpoint gates and a starting grid.
 *
 * The grid is intentionally independent from the artwork: the renderer draws a
 * pretty ribbon, the physics reads cells.
 */

import { catmullRom, TAU } from './math.js';
import { SURFACE_INDEX, SURFACE_LIST, type SurfaceId } from './surfaces.js';
import { getTrack, type TrackDef } from './tracks.js';

export const GRID_CELL = 8;

export interface Grid {
  cell: number;
  cols: number;
  rows: number;
  surface: Uint8Array;
  solid: Uint8Array;
  /**
   * 1 for every cell that is part of the drivable track: the road ribbon, the
   * painted runoff around it, and any drivable patch laid on top.
   *
   * This is a mask rather than a list of "road-ish" surface ids on purpose: a
   * rally circuit whose road is *gravel* and a desert circuit whose road is
   * *dirt* are just as much "on the road" as a tarmac circuit, and a hard-coded
   * whitelist silently declares two thirds of the game's circuits undrivable.
   */
  road: Uint8Array;
}

export interface PathPoint {
  x: number;
  y: number;
  /** Local road width (full width). */
  w: number;
  /** Arc length from the start line. */
  dist: number;
  /** Tangent direction in radians. */
  angle: number;
  /** Signed curvature (1/radius); positive turns left. */
  curvature: number;
}

export interface Checkpoint {
  /** Index into `path`. */
  index: number;
  x: number;
  y: number;
  angle: number;
  halfWidth: number;
  dist: number;
}

export interface RacingPoint {
  x: number;
  y: number;
  /** Absolute corner radius in px; Infinity for a straight. */
  radius: number;
  /** Sign of the turn (-1 right, +1 left) used to pick the apex side. */
  turn: number;
  dist: number;
  index: number;
}

export interface SpawnSlot {
  x: number;
  y: number;
  angle: number;
  /** Centrelines sample the slot sits next to. */
  index: number;
}

export interface BuiltTrack {
  def: TrackDef;
  grid: Grid;
  path: PathPoint[];
  totalLength: number;
  checkpoints: Checkpoint[];
  racingLine: RacingPoint[];
  spawns: SpawnSlot[];
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
  /** Bucketed index for fast nearest-path queries. */
  buckets: PathBuckets;
}

interface PathBuckets {
  cell: number;
  cols: number;
  rows: number;
  indices: number[][];
}

const CACHE = new Map<string, BuiltTrack>();

/** Returns a cached built track for the given id. */
export function buildTrack(id: string): BuiltTrack {
  const cached = CACHE.get(id);
  if (cached) return cached;
  const built = buildTrackFromDef(getTrack(id));
  CACHE.set(id, built);
  return built;
}

export function buildTrackFromDef(def: TrackDef): BuiltTrack {
  const path = resampleCenterline(def);
  const totalLength = path.length > 0 ? path[path.length - 1]!.dist + spacingOf(path, def) : 0;
  const grid = rasterise(def, path, totalLength);
  const checkpoints = buildCheckpoints(def, path, totalLength);
  const racingLine = buildRacingLine(def, path);
  const spawns = buildSpawns(def, path);
  const buckets = buildBuckets(path, def);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of path) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return {
    def, grid, path, totalLength, checkpoints, racingLine, spawns,
    bounds: { minX, minY, maxX, maxY }, buckets,
  };
}

function spacingOf(path: PathPoint[], _def: TrackDef): number {
  if (path.length < 2) return 1;
  return Math.hypot(path[1]!.x - path[0]!.x, path[1]!.y - path[0]!.y);
}

/** How densely to sample the smooth centreline before re-spacing it. */
const DENSE_SAMPLES = 2600;
const PATH_SPACING = 8;

function resampleCenterline(def: TrackDef): PathPoint[] {
  const n = def.nodes.length;
  const xs = def.nodes.map((p) => p.x);
  const ys = def.nodes.map((p) => p.y);
  const widths = def.nodes.map((p) => p.w ?? def.roadWidth);

  // Dense smooth sample of the closed Catmull-Rom spline. The control points
  // are passed without duplication so the curve wraps seamlessly.
  const denseX: number[] = [];
  const denseY: number[] = [];
  const denseW: number[] = [];
  for (let i = 0; i <= DENSE_SAMPLES; i++) {
    const t = i / DENSE_SAMPLES;
    const p = catmullRom(xs, ys, t, true);
    denseX.push(p.x);
    denseY.push(p.y);
    // Interpolate the per-node width with the same parameterisation.
    const scaled = t * n;
    const i0 = Math.floor(scaled) % n;
    const i1 = (i0 + 1) % n;
    const f = scaled - Math.floor(scaled);
    denseW.push(widths[i0]! * (1 - f) + widths[i1]! * f);
  }

  // Re-space to a uniform arc length.
  const out: PathPoint[] = [];
  let acc = 0;
  let nextAt = 0;
  for (let i = 0; i < denseX.length - 1; i++) {
    if (acc >= nextAt) {
      out.push({
        x: denseX[i]!,
        y: denseY[i]!,
        w: denseW[i]!,
        dist: acc,
        angle: 0,
        curvature: 0,
      });
      nextAt += PATH_SPACING;
    }
    acc += Math.hypot(denseX[i + 1]! - denseX[i]!, denseY[i + 1]! - denseY[i]!);
  }
  // Close the loop by wrapping the last points back to the first.
  if (out.length > 4) {
    const last = out[out.length - 1]!;
    const first = out[0]!;
    const gap = Math.hypot(first.x - last.x, first.y - last.y);
    if (gap < PATH_SPACING * 2) out.pop();
  }

  // Tangents and curvature from finite differences on the closed loop.
  const m = out.length;
  for (let i = 0; i < m; i++) {
    const prev = out[(i - 1 + m) % m]!;
    const next = out[(i + 1) % m]!;
    out[i]!.angle = Math.atan2(next.y - prev.y, next.x - prev.x);
  }
  for (let i = 0; i < m; i++) {
    const prev = out[(i - 1 + m) % m]!;
    const cur = out[i]!;
    const next = out[(i + 1) % m]!;
    const a1 = Math.atan2(cur.y - prev.y, cur.x - prev.x);
    const a2 = Math.atan2(next.y - cur.y, next.x - cur.x);
    let d = a2 - a1;
    while (d > Math.PI) d -= TAU;
    while (d < -Math.PI) d += TAU;
    const ds = (Math.hypot(cur.x - prev.x, cur.y - prev.y) + Math.hypot(next.x - cur.x, next.y - cur.y)) / 2;
    cur.curvature = ds > 1e-6 ? d / ds : 0;
  }
  return out;
}

function idxFor(s: SurfaceId): number {
  return SURFACE_INDEX[s];
}

function rasterise(def: TrackDef, path: PathPoint[], _totalLength: number): Grid {
  const cell = GRID_CELL;
  const cols = Math.ceil(def.width / cell);
  const rows = Math.ceil(def.height / cell);
  const surface = new Uint8Array(cols * rows).fill(idxFor(def.base));
  const solid = new Uint8Array(cols * rows);
  /**
   * Cells that have been deliberately painted (road, shoulder or a patch) as
   * opposed to merely holding the base fill. The barrier pass must only seal
   * *unpainted* cells — otherwise a patch painted with the same surface id as
   * the base would silently turn into a wall.
   */
  const touched = new Uint8Array(cols * rows);
  /** Cells that are drivable: road ribbon, runoff, and drivable patches. */
  const road = new Uint8Array(cols * rows);

  const stamp = (cx: number, cy: number, radius: number, surfaceIdx: number, isRoad: boolean): void => {
    const r2 = radius * radius;
    const minC = Math.max(0, Math.floor((cx - radius) / cell));
    const maxC = Math.min(cols - 1, Math.ceil((cx + radius) / cell));
    const minR = Math.max(0, Math.floor((cy - radius) / cell));
    const maxR = Math.min(rows - 1, Math.ceil((cy + radius) / cell));
    for (let ry = minR; ry <= maxR; ry++) {
      const py = ry * cell + cell / 2;
      const dy = py - cy;
      for (let rx = minC; rx <= maxC; rx++) {
        const px = rx * cell + cell / 2;
        const dx = px - cx;
        if (dx * dx + dy * dy > r2) continue;
        const i = ry * cols + rx;
        surface[i] = surfaceIdx;
        touched[i] = 1;
        if (isRoad) road[i] = 1;
      }
    }
  };

  // Pass 1: shoulder / runoff. Pass 2: road (so road always wins where both touch).
  // Both passes are drivable, so both mark the track mask; a car that runs a
  // little wide onto the runoff is still on the circuit.
  const shoulderIdx = idxFor(def.shoulder);
  const roadIdx = idxFor(def.road);
  for (const p of path) stamp(p.x, p.y, p.w / 2 + def.runoff, shoulderIdx, true);
  for (const p of path) stamp(p.x, p.y, p.w / 2, roadIdx, true);

  // Patches can paint surfaces or act as solid obstacles.
  for (const patch of def.patches ?? []) {
    const sIdx = idxFor(patch.surface);
    if (patch.shape === 'ellipse') {
      const rot = patch.angle ?? 0;
      const minC = Math.max(0, Math.floor((patch.x - patch.w) / cell));
      const maxC = Math.min(cols - 1, Math.ceil((patch.x + patch.w) / cell));
      const minR = Math.max(0, Math.floor((patch.y - patch.h) / cell));
      const maxR = Math.min(rows - 1, Math.ceil((patch.y + patch.h) / cell));
      const cos = Math.cos(-rot);
      const sin = Math.sin(-rot);
      const rw = patch.w / 2;
      const rh = patch.h / 2;
      for (let ry = minR; ry <= maxR; ry++) {
        const py = ry * cell + cell / 2;
        for (let rx = minC; rx <= maxC; rx++) {
          const px = rx * cell + cell / 2;
          const dx0 = px - patch.x;
          const dy0 = py - patch.y;
          const dx = dx0 * cos - dy0 * sin;
          const dy = dx0 * sin + dy0 * cos;
          if ((dx * dx) / (rw * rw) + (dy * dy) / (rh * rh) > 1) continue;
          const i = ry * cols + rx;
          surface[i] = sIdx;
          touched[i] = 1;
          if (patch.solid) solid[i] = 1;
          else road[i] = 1;
        }
      }
    } else if (patch.shape === 'rect') {
      const rot = patch.angle ?? 0;
      const cos = Math.cos(-rot);
      const sin = Math.sin(-rot);
      const reach = Math.hypot(patch.w, patch.h) / 2 + cell;
      const minC = Math.max(0, Math.floor((patch.x - reach) / cell));
      const maxC = Math.min(cols - 1, Math.ceil((patch.x + reach) / cell));
      const minR = Math.max(0, Math.floor((patch.y - reach) / cell));
      const maxR = Math.min(rows - 1, Math.ceil((patch.y + reach) / cell));
      for (let ry = minR; ry <= maxR; ry++) {
        const py = ry * cell + cell / 2;
        for (let rx = minC; rx <= maxC; rx++) {
          const px = rx * cell + cell / 2;
          const dx0 = px - patch.x;
          const dy0 = py - patch.y;
          const dx = dx0 * cos - dy0 * sin;
          const dy = dx0 * sin + dy0 * cos;
          if (Math.abs(dx) > patch.w / 2 || Math.abs(dy) > patch.h / 2) continue;
          const i = ry * cols + rx;
          surface[i] = sIdx;
          touched[i] = 1;
          if (patch.solid) solid[i] = 1;
          else road[i] = 1;
        }
      }
    } else if (patch.shape === 'poly' && patch.points) {
      const pts = patch.points.map(([x, y]) => ({ x: patch.x + x, y: patch.y + y }));
      const reach = Math.max(patch.w, patch.h) + cell * 2;
      const minC = Math.max(0, Math.floor((patch.x - reach) / cell));
      const maxC = Math.min(cols - 1, Math.ceil((patch.x + reach) / cell));
      const minR = Math.max(0, Math.floor((patch.y - reach) / cell));
      const maxR = Math.min(rows - 1, Math.ceil((patch.y + reach) / cell));
      for (let ry = minR; ry <= maxR; ry++) {
        const py = ry * cell + cell / 2;
        for (let rx = minC; rx <= maxC; rx++) {
          const px = rx * cell + cell / 2;
          if (!pointInPoly(px, py, pts)) continue;
          const i = ry * cols + rx;
          surface[i] = sIdx;
          touched[i] = 1;
          if (patch.solid) solid[i] = 1;
          else road[i] = 1;
        }
      }
    }
  }

  if (def.barriers === 'edges') {
    const wallIdx = idxFor('wall');
    for (let i = 0; i < surface.length; i++) {
      if (touched[i] === 0) {
        surface[i] = wallIdx;
        solid[i] = 1;
      }
    }
  } else {
    // Without barriers, seal the outer track bounds so cars cannot leave the
    // map. Painted cells win so a road that reaches the edge still works.
    const wallIdx = idxFor('wall');
    for (let i = 0; i < surface.length; i++) {
      if (touched[i] !== 0) continue;
      const col = i % cols;
      const row = (i - col) / cols;
      if (col === 0 || row === 0 || col === cols - 1 || row === rows - 1) {
        solid[i] = 1;
        surface[i] = wallIdx;
      }
    }
  }

  return { cell, cols, rows, surface, solid, road };
}

function pointInPoly(px: number, py: number, pts: Array<{ x: number; y: number }>): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i]!.x, yi = pts[i]!.y;
    const xj = pts[j]!.x, yj = pts[j]!.y;
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function buildCheckpoints(def: TrackDef, path: PathPoint[], totalLength: number): Checkpoint[] {
  const count = Math.max(3, def.checkpointCount);
  const out: Checkpoint[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.floor((i / count) * path.length) % path.length;
    const p = path[idx]!;
    out.push({
      index: idx,
      x: p.x,
      y: p.y,
      angle: p.angle,
      halfWidth: p.w / 2 + def.runoff * 0.5,
      dist: (i / count) * totalLength,
    });
  }
  return out;
}

/**
 * Builds a relaxed racing line by iteratively pulling each centreline point
 * toward the midpoint of its neighbours while staying inside the corridor.
 * This produces a good approximation of the classic out-in-out line and, more
 * importantly, is stable for the AI to follow.
 */
function buildRacingLine(def: TrackDef, path: PathPoint[]): RacingPoint[] {
  const n = path.length;
  const margin = 14;
  const xs = new Float64Array(n);
  const ys = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    xs[i] = path[i]!.x;
    ys[i] = path[i]!.y;
  }
  const iterations = 90;
  for (let it = 0; it < iterations; it++) {
    for (let i = 0; i < n; i++) {
      const p = (i - 1 + n) % n;
      const q = (i + 1) % n;
      const mx = (xs[p]! + xs[q]!) / 2;
      const my = (ys[p]! + ys[q]!) / 2;
      const k = 0.35;
      let nx = xs[i]! + (mx - xs[i]!) * k;
      let ny = ys[i]! + (my - ys[i]!) * k;
      // Clamp inside the corridor.
      const c = path[i]!;
      const lim = Math.max(4, c.w / 2 - margin);
      const dx = nx - c.x;
      const dy = ny - c.y;
      const len = Math.hypot(dx, dy);
      if (len > lim) {
        nx = c.x + (dx / len) * lim;
        ny = c.y + (dy / len) * lim;
      }
      xs[i] = nx;
      ys[i] = ny;
    }
  }
  const out: RacingPoint[] = [];
  for (let i = 0; i < n; i++) {
    const c = path[i]!;
    const curv = c.curvature;
    const radius = Math.abs(curv) > 1e-6 ? Math.min(4000, 1 / Math.abs(curv)) : Infinity;
    out.push({ x: xs[i]!, y: ys[i]!, radius, turn: curv >= 0 ? 1 : -1, dist: c.dist, index: i });
  }
  return out;
}

function buildSpawns(def: TrackDef, path: PathPoint[]): SpawnSlot[] {
  const out: SpawnSlot[] = [];
  const n = path.length;
  const rowGap = 30;
  const colGap = def.roadWidth * 0.24;
  for (let i = 0; i < 12; i++) {
    const row = Math.floor(i / 2);
    const side = i % 2 === 0 ? -1 : 1;
    const targetDist = -((row + 1) * rowGap + 12);
    // Walk backwards along the path from the start line.
    let idx = Math.round(targetDist / PATH_SPACING);
    idx = ((idx % n) + n) % n;
    const p = path[idx]!;
    const nx = -Math.sin(p.angle);
    const ny = Math.cos(p.angle);
    out.push({
      x: p.x + nx * colGap * side,
      y: p.y + ny * colGap * side,
      angle: p.angle,
      index: idx,
    });
  }
  return out;
}

function buildBuckets(path: PathPoint[], def: TrackDef): PathBuckets {
  const cell = 96;
  const cols = Math.ceil(def.width / cell) + 1;
  const rows = Math.ceil(def.height / cell) + 1;
  const indices: number[][] = new Array(cols * rows);
  for (let i = 0; i < path.length; i++) {
    const p = path[i]!;
    const cx = Math.min(cols - 1, Math.max(0, Math.floor(p.x / cell)));
    const cy = Math.min(rows - 1, Math.max(0, Math.floor(p.y / cell)));
    const key = cy * cols + cx;
    (indices[key] ??= []).push(i);
  }
  return { cell, cols, rows, indices };
}

/** Nearest centreline sample index to a world position (fast, bucketed). */
export function nearestPathIndex(track: BuiltTrack, x: number, y: number): number {
  const b = track.buckets;
  const cx = Math.min(b.cols - 1, Math.max(0, Math.floor(x / b.cell)));
  const cy = Math.min(b.rows - 1, Math.max(0, Math.floor(y / b.cell)));
  let best = -1;
  let bestD = Infinity;
  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) {
      const gx = cx + ox;
      const gy = cy + oy;
      if (gx < 0 || gy < 0 || gx >= b.cols || gy >= b.rows) continue;
      const list = b.indices[gy * b.cols + gx];
      if (!list) continue;
      for (const i of list) {
        const p = track.path[i]!;
        const d = (p.x - x) * (p.x - x) + (p.y - y) * (p.y - y);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
    }
  }
  if (best >= 0) return best;
  // Fall back to a coarse scan when the query point is far outside.
  const step = 7;
  for (let i = 0; i < track.path.length; i += step) {
    const p = track.path[i]!;
    const d = (p.x - x) * (p.x - x) + (p.y - y) * (p.y - y);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best < 0 ? 0 : best;
}

/** How far a car may sit from the centreline before we distrust a local search. */
const HINT_SEARCH_ESCAPE = 220;

/**
 * Nearest centreline index, searched only in a window around `hint`.
 *
 * Several circuits double back on themselves (a hairpin beside a straight), so
 * a purely global "nearest point" query can snap to the *other* leg of the
 * track and send a driver off in the wrong direction. A car can only ever
 * advance a couple of samples per frame, so constraining the search to a window
 * around the previous index is both correct and much cheaper. If the car is
 * genuinely nowhere near that stretch (a respawn, or a long slide off the map)
 * the search widens to the whole path again.
 */
export function nearestPathIndexNear(
  track: BuiltTrack,
  x: number,
  y: number,
  hint: number,
  window = 48,
): number {
  const n = track.path.length;
  if (n === 0) return 0;
  if (hint < 0 || hint >= n) return nearestPathIndex(track, x, y);
  let best = hint;
  let bestD = Infinity;
  for (let o = -window; o <= window; o++) {
    const i = ((hint + o) % n + n) % n;
    const p = track.path[i]!;
    const d = (p.x - x) * (p.x - x) + (p.y - y) * (p.y - y);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  if (bestD > HINT_SEARCH_ESCAPE * HINT_SEARCH_ESCAPE) return nearestPathIndex(track, x, y);
  return best;
}

export function cellIndex(grid: Grid, x: number, y: number): number {
  const cx = Math.floor(x / grid.cell);
  const cy = Math.floor(y / grid.cell);
  if (cx < 0 || cy < 0 || cx >= grid.cols || cy >= grid.rows) return -1;
  return cy * grid.cols + cx;
}

/** Surface definition under a world position. */
export function surfaceAt(track: BuiltTrack, x: number, y: number): SurfaceId {
  const i = cellIndex(track.grid, x, y);
  if (i < 0) return 'wall';
  return SURFACE_LIST[track.grid.surface[i]!] ?? 'asphalt';
}

export function isSolidAt(track: BuiltTrack, x: number, y: number): boolean {
  const i = cellIndex(track.grid, x, y);
  if (i < 0) return true;
  return track.grid.solid[i] === 1;
}

/**
 * True when a world position is on the circuit's racing surface (the road
 * ribbon or a drivable patch painted over it), as opposed to the scenery the
 * road is cut into. Works for every surface palette, so a gravel rally stage
 * and a tarmac street circuit are both handled correctly.
 */
export function isRoadAt(track: BuiltTrack, x: number, y: number): boolean {
  const i = cellIndex(track.grid, x, y);
  if (i < 0) return false;
  return track.grid.road[i] === 1;
}

/** True when any cell within `radius` of the point is solid. */
export function cellNearSolid(track: BuiltTrack, x: number, y: number, radius: number): boolean {
  const g = track.grid;
  const minC = Math.max(0, Math.floor((x - radius) / g.cell));
  const maxC = Math.min(g.cols - 1, Math.floor((x + radius) / g.cell));
  const minR = Math.max(0, Math.floor((y - radius) / g.cell));
  const maxR = Math.min(g.rows - 1, Math.floor((y + radius) / g.cell));
  for (let ry = minR; ry <= maxR; ry++) {
    for (let rx = minC; rx <= maxC; rx++) {
      if (g.solid[ry * g.cols + rx] === 1) return true;
    }
  }
  return false;
}

